import type {
  BookingItemOverviewListApiData,
  BookingItemOverviewListApiResponses,
  CapacityContributionBatchPatchApiData,
  CapacityContributionBatchPatchApiResponses,
  CapacityContributionCreateBaselineApiData,
  CapacityContributionCreateBaselineApiResponses,
  CapacityContributionEventsListApiData,
  CapacityContributionEventsListApiResponses,
  CapacityContributionGetApiData,
  CapacityContributionGetApiResponses,
  CapacityContributionListApiData,
  CapacityContributionListApiResponses,
  CapacityContributionMinimalListApiData,
  CapacityContributionMinimalListApiResponses,
  CapacityContributionPatchApiData,
  CapacityContributionPatchApiResponses,
  CapacityLatestDateApiData,
  CapacityLatestDateApiResponses,
  GarageShiftForServiceWorkerListApiData,
  GarageShiftForServiceWorkerListApiResponses,
  MobileCoverageDayListApiData,
  MobileCoverageDayListApiResponses,
  MobileServiceAvailabilityApiData,
  MobileServiceAvailabilityApiResponses,
  MobileWaitTimeApiData,
  MobileWaitTimeApiResponses,
  RouteChangeAssignedServiceWorkerApiData,
  RouteChangeAssignedServiceWorkerApiResponses,
  RouteChangeCapacityContributionApiData,
  RouteChangeCapacityContributionApiResponses,
  ServiceDepartmentCapacityOverviewApiData,
  ServiceDepartmentCapacityOverviewApiResponses,
  ServiceVehicleHoldersListApiData,
  ServiceVehicleHoldersListApiResponses,
  StationaryCapacitySlotContributionsListApiData,
  StationaryCapacitySlotContributionsListApiResponses,
  StationaryCapacitySlotsListApiData,
  StationaryCapacitySlotsListApiResponses,
  StationaryCapacityUtilizationListApiData,
  StationaryCapacityUtilizationListApiResponses,
  StationaryScheduleApiData,
  StationaryScheduleApiResponses,
  WorkerShiftCreateApiData,
  WorkerShiftCreateApiResponses,
  WorkerShiftDatesForServiceWorkerListApiData,
  WorkerShiftDatesForServiceWorkerListApiResponses,
  WorkerShiftDeleteApiData,
  WorkerShiftDeleteApiResponses,
  WorkerShiftEventsListApiData,
  WorkerShiftEventsListApiResponses,
  WorkerShiftExportApiData,
  WorkerShiftExportApiResponses,
  WorkerShiftForServiceWorkerListApiData,
  WorkerShiftForServiceWorkerListApiResponses,
  WorkerShiftGetApiData,
  WorkerShiftGetApiResponses,
  WorkerShiftListApiData,
  WorkerShiftListApiResponses,
  WorkerShiftPatchApiData,
  WorkerShiftPatchApiResponses,
  WorkerShiftWarningsCreatePreviewApiData,
  WorkerShiftWarningsCreatePreviewApiResponses,
  WorkerShiftWarningsGetApiData,
  WorkerShiftWarningsGetApiResponses,
  WorkerShiftWarningsUpdatePreviewApiData,
  WorkerShiftWarningsUpdatePreviewApiResponses,
  WorkingHoursOverviewApiData,
  WorkingHoursOverviewApiResponses
} from '@navio/nsdk';

import { downloadToExcel } from '../../downloadToExcel';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export const getCapacityUrls = {
  getMobileServiceAvailability: dataEndpoint<MobileServiceAvailabilityApiData, MobileServiceAvailabilityApiResponses>({
    getUrl: (queryParams) => apiUrl('mobile-coverage/service-availability/', queryParams)
  }),
  getMobileWaitTime: dataEndpoint<MobileWaitTimeApiData, MobileWaitTimeApiResponses>({
    getUrl: (queryParams) => apiUrl('mobile-coverage/wait-times/', queryParams)
  }),
  // Partner always needs ADAPTER pool tags on contribution reads.
  // Opt-in is forced here so call sites never probe adapters just to show badges.
  getPaginatedCapacityContributions: dataEndpoint<
    CapacityContributionListApiData,
    CapacityContributionListApiResponses
  >({
    getUrl: (queryParams) =>
      apiUrl('capacity-contributions/', {
        ...queryParams,
        includeAdapterCapabilities: true
      })
  }),
  getPaginatedCapacityContributionsMinimal: dataEndpoint<
    CapacityContributionMinimalListApiData,
    CapacityContributionMinimalListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('capacity-contributions/minimal/', queryParams)
  }),
  // The caller's garage shifts from today through a week out, with the day's booked cars (worker app home).
  getGarageShiftsForServiceWorker: dataEndpoint<
    GarageShiftForServiceWorkerListApiData,
    GarageShiftForServiceWorkerListApiResponses
  >({
    getUrl: () => apiUrl('capacity-contributions/garage-shifts-for-service-worker/')
  }),
  getServiceVehicleHolders: dataEndpoint<ServiceVehicleHoldersListApiData, ServiceVehicleHoldersListApiResponses>({
    getUrl: (queryParams) => apiUrl('capacity-contributions/service-vehicle-holders/', queryParams)
  }),
  getCapacityOverview: dataEndpoint<
    ServiceDepartmentCapacityOverviewApiData,
    ServiceDepartmentCapacityOverviewApiResponses
  >({
    getUrl: ({ serviceDepartmentId, ...queryParams }) =>
      apiUrl(`service-departments/${serviceDepartmentId}/capacity-overview/`, queryParams)
  }),
  getMobileCoverageDays: dataEndpoint<MobileCoverageDayListApiData, MobileCoverageDayListApiResponses>({
    getUrl: (queryParams) => apiUrl('mobile-coverage/days/', queryParams)
  }),
  getBookingItemsOverview: dataEndpoint<BookingItemOverviewListApiData, BookingItemOverviewListApiResponses>({
    getUrl: (queryParams) => apiUrl('booking-items/overview/', queryParams)
  }),
  postChangeAssignedServiceWorker: responseEndpoint<
    RouteChangeAssignedServiceWorkerApiData,
    RouteChangeAssignedServiceWorkerApiResponses
  >({
    getUrl: ({ routeId }) => apiUrl(`routes/${routeId}/change-assigned-service-worker/`),
    getBody: ({ routeId: _, ...body }) => body
  }),
  postChangeCapacityContribution: responseEndpoint<
    RouteChangeCapacityContributionApiData,
    RouteChangeCapacityContributionApiResponses
  >({
    getUrl: ({ routeId }) => apiUrl(`routes/${routeId}/change-capacity-contribution/`),
    getBody: ({ routeId: _, ...body }) => body
  }),
  // A worker's day: time, breaks, note and its (optional) bookable time.
  postWorkerShift: responseEndpoint<WorkerShiftCreateApiData, WorkerShiftCreateApiResponses>({
    getUrl: () => apiUrl('worker-shifts/'),
    getBody: (data) => data
  }),
  getPaginatedWorkerShifts: dataEndpoint<WorkerShiftListApiData, WorkerShiftListApiResponses>({
    getUrl: (queryParams) =>
      apiUrl('worker-shifts/', {
        ...queryParams,
        includeAdapterCapabilities: true
      })
  }),
  // The work plan (Working Environment Act § 10-3): the shift list's scope, search and filters, unpaginated.
  getWorkerShiftsExport: dataEndpoint<WorkerShiftExportApiData, WorkerShiftExportApiResponses>({
    getUrl: (queryParams) => apiUrl('worker-shifts/export/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  getWorkerShift: dataEndpoint<WorkerShiftGetApiData, WorkerShiftGetApiResponses>({
    getUrl: ({ workerShiftId }) =>
      apiUrl(`worker-shifts/${workerShiftId}/`, {
        includeAdapterCapabilities: true
      })
  }),
  getPaginatedWorkerShiftEvents: dataEndpoint<WorkerShiftEventsListApiData, WorkerShiftEventsListApiResponses>({
    getUrl: ({ workerShiftId, ...queryParams }) => apiUrl(`worker-shifts/${workerShiftId}/events/`, queryParams)
  }),
  patchWorkerShift: responseEndpoint<WorkerShiftPatchApiData, WorkerShiftPatchApiResponses>({
    getUrl: ({ workerShiftId }) => apiUrl(`worker-shifts/${workerShiftId}/`),
    getBody: ({ workerShiftId: _, ...body }) => body
  }),
  // Working Environment Act warnings (backend capacity ADR 0011): a create's or a PATCH's own body
  // previewed without writing, or a stored shift as planned now. Never a refusal.
  postWorkerShiftWarningsPreview: responseEndpoint<
    WorkerShiftWarningsCreatePreviewApiData,
    WorkerShiftWarningsCreatePreviewApiResponses
  >({
    getUrl: () => apiUrl('worker-shifts/warnings/'),
    getBody: (data) => data
  }),
  postWorkerShiftUpdateWarningsPreview: responseEndpoint<
    WorkerShiftWarningsUpdatePreviewApiData,
    WorkerShiftWarningsUpdatePreviewApiResponses
  >({
    getUrl: ({ workerShiftId }) => apiUrl(`worker-shifts/${workerShiftId}/warnings/`),
    getBody: ({ workerShiftId: _, ...body }) => body
  }),
  getWorkerShiftWarnings: dataEndpoint<WorkerShiftWarningsGetApiData, WorkerShiftWarningsGetApiResponses>({
    getUrl: ({ workerShiftId }) => apiUrl(`worker-shifts/${workerShiftId}/warnings/`)
  }),
  deleteWorkerShift: dataEndpoint<WorkerShiftDeleteApiData, WorkerShiftDeleteApiResponses>({
    getUrl: ({ workerShiftId }) => apiUrl(`worker-shifts/${workerShiftId}/`)
  }),
  getWorkerShiftDates: dataEndpoint<
    WorkerShiftDatesForServiceWorkerListApiData,
    WorkerShiftDatesForServiceWorkerListApiResponses
  >({
    getUrl: ({ serviceWorkerId, ...rest }) => apiUrl(`service-workers/${serviceWorkerId}/worker-shift-dates/`, rest)
  }),
  getWorkerShiftsForDate: dataEndpoint<
    WorkerShiftForServiceWorkerListApiData,
    WorkerShiftForServiceWorkerListApiResponses
  >({
    getUrl: ({ serviceWorkerId, ...rest }) => apiUrl(`service-workers/${serviceWorkerId}/worker-shifts/`, rest)
  }),
  getLatestCapacityDate: dataEndpoint<CapacityLatestDateApiData, CapacityLatestDateApiResponses>({
    getUrl: () => apiUrl('capacity/latest-date/')
  }),
  postCapacityContributionBaseline: responseEndpoint<
    CapacityContributionCreateBaselineApiData,
    CapacityContributionCreateBaselineApiResponses
  >({
    getUrl: () => apiUrl('capacity-contributions/baseline/'),
    getBody: (data) => data
  }),
  patchCapacityContribution: responseEndpoint<CapacityContributionPatchApiData, CapacityContributionPatchApiResponses>({
    getUrl: ({ capacityContributionId }) => apiUrl(`capacity-contributions/${capacityContributionId}/`),
    getBody: (data) => data
  }),
  patchBatchCapacityContributions: responseEndpoint<
    CapacityContributionBatchPatchApiData,
    CapacityContributionBatchPatchApiResponses
  >({
    getUrl: () => apiUrl('capacity-contributions/batch/'),
    getBody: (data) => data
  }),
  getCapacityContribution: dataEndpoint<CapacityContributionGetApiData, CapacityContributionGetApiResponses>({
    getUrl: ({ capacityContributionId }) =>
      apiUrl(`capacity-contributions/${capacityContributionId}/`, {
        includeAdapterCapabilities: true
      })
  }),
  getPaginatedCapacityContributionEvents: dataEndpoint<
    CapacityContributionEventsListApiData,
    CapacityContributionEventsListApiResponses
  >({
    getUrl: ({ capacityContributionId, ...queryParams }) =>
      apiUrl(`capacity-contributions/${capacityContributionId}/events/`, queryParams)
  }),
  // Stationary capacity queries (canonical capacity HTTP surface)
  getStationaryCapacitySlots: dataEndpoint<StationaryCapacitySlotsListApiData, StationaryCapacitySlotsListApiResponses>(
    {
      getUrl: ({ garageId, ...queryParams }) =>
        apiUrl('capacity/queries/stationary/slots/', { ...queryParams, garageId })
    }
  ),
  /**
   * Authenticated free slots with named supply rows (worker/baseline identity).
   * Public sell must use getStationaryCapacitySlots (counts only, no PII).
   */
  getStationaryCapacitySlotContributions: dataEndpoint<
    StationaryCapacitySlotContributionsListApiData,
    StationaryCapacitySlotContributionsListApiResponses
  >({
    getUrl: ({ garageId, ...queryParams }) =>
      apiUrl('capacity/queries/stationary/slot-contributions/', { ...queryParams, garageId })
  }),
  getStationaryCapacityUtilization: dataEndpoint<
    StationaryCapacityUtilizationListApiData,
    StationaryCapacityUtilizationListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('capacity/queries/stationary/utilization/', queryParams)
  }),
  getStationarySchedule: dataEndpoint<StationaryScheduleApiData, StationaryScheduleApiResponses>({
    getUrl: (queryParams) => apiUrl('capacity/queries/stationary/schedule/', queryParams)
  }),
  /**
   * Staffing board for a date range. Supply already has the floor applied
   * server-side — never add `namedSeconds` to `expectedStaffingSeconds`, they
   * describe the same seats. Not a sell surface (backend ADR 0008).
   */
  getWorkingHoursOverview: dataEndpoint<WorkingHoursOverviewApiData, WorkingHoursOverviewApiResponses>({
    getUrl: (queryParams) => apiUrl('capacity/queries/working-hours-overview/', queryParams)
  })
};
